import { useCallback, useEffect, useState } from 'react';

import { getSnapshotComparisonApplications } from '../../../api/snapshotChangeDetection';
import {
  DEFAULT_FILTERS,
  DEFAULT_PAGE_SIZE,
  LIST_STATE_STORAGE_KEY,
  PAGE_SIZE_OPTIONS
} from '../constants';
import {
  SnapshotComparisonApplication,
  SnapshotComparisonFilters,
  TriStateFilter,
  CenterFilter,
  ExtractionQcStatusFilter
} from '../types';
import {
  isCenterFilter,
  isExtractionQcStatusFilter,
  isTriStateFilter,
  parseCenterFilter,
  parseExtractionQcStatusFilter,
  parseTriStateFilter
} from '../utils';

type PersistedListState = {
  filters: SnapshotComparisonFilters;
  page: number;
  pageSize: number;
};

const loadPersistedListState = (): PersistedListState | null => {
  try {
    const raw = sessionStorage.getItem(LIST_STATE_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    return JSON.parse(raw) as PersistedListState;
  } catch {
    return null;
  }
};

const getInitialListState = (): PersistedListState => {
  const stored = loadPersistedListState();
  if (!stored) {
    return { filters: DEFAULT_FILTERS, page: 0, pageSize: DEFAULT_PAGE_SIZE };
  }

  return {
    filters: {
      applicationNumber:
        typeof stored.filters?.applicationNumber === 'string'
          ? stored.filters.applicationNumber
          : DEFAULT_FILTERS.applicationNumber,
      center: isCenterFilter(stored.filters?.center)
        ? stored.filters.center
        : DEFAULT_FILTERS.center,
      qcStatus: isTriStateFilter(stored.filters?.qcStatus)
        ? stored.filters.qcStatus
        : DEFAULT_FILTERS.qcStatus,
      changeDetectionStatus: isTriStateFilter(stored.filters?.changeDetectionStatus)
        ? stored.filters.changeDetectionStatus
        : DEFAULT_FILTERS.changeDetectionStatus,
      escalatedStatus: isTriStateFilter(stored.filters?.escalatedStatus)
        ? stored.filters.escalatedStatus
        : DEFAULT_FILTERS.escalatedStatus,
      allVerdictFalse: isTriStateFilter(stored.filters?.allVerdictFalse)
        ? stored.filters.allVerdictFalse
        : DEFAULT_FILTERS.allVerdictFalse,
      extractionQcStatus: isExtractionQcStatusFilter(stored.filters?.extractionQcStatus)
        ? stored.filters.extractionQcStatus
        : DEFAULT_FILTERS.extractionQcStatus,
      dataExtracted: isTriStateFilter(stored.filters?.dataExtracted)
        ? stored.filters.dataExtracted
        : DEFAULT_FILTERS.dataExtracted
    },
    page: typeof stored.page === 'number' && stored.page >= 0 ? stored.page : 0,
    pageSize:
      typeof stored.pageSize === 'number' && PAGE_SIZE_OPTIONS.includes(stored.pageSize)
        ? stored.pageSize
        : DEFAULT_PAGE_SIZE
  };
};

const persistListState = (state: PersistedListState) => {
  sessionStorage.setItem(LIST_STATE_STORAGE_KEY, JSON.stringify(state));
};

/** Server-paginated application list for the Snapshot Comparison landing page. */
const useSnapshotComparisonList = () => {
  const initialState = getInitialListState();
  const [filters, setFilters] = useState<SnapshotComparisonFilters>(initialState.filters);
  const [page, setPage] = useState(initialState.page);
  const [pageSize, setPageSize] = useState(initialState.pageSize);
  const [rows, setRows] = useState<SnapshotComparisonApplication[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    persistListState({ filters, page, pageSize });
  }, [filters, page, pageSize]);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getSnapshotComparisonApplications({
        center: parseCenterFilter(filters.center),
        application_number: filters.applicationNumber.trim() || undefined,
        qc_status: parseTriStateFilter(filters.qcStatus),
        change_detection_status: parseTriStateFilter(filters.changeDetectionStatus),
        escalated_status: parseTriStateFilter(filters.escalatedStatus),
        all_verdict_false: parseTriStateFilter(filters.allVerdictFalse),
        extraction_qc_status: parseExtractionQcStatusFilter(filters.extractionQcStatus),
        data_extracted: parseTriStateFilter(filters.dataExtracted),
        page,
        page_size: pageSize
      });

      if (response?.data?.status === 200) {
        setRows(response.data.body?.results ?? []);
        setTotal(response.data.body?.total ?? 0);
        return;
      }

      setRows([]);
      setTotal(0);
      setError('Unable to load snapshot comparison applications.');
    } catch {
      setRows([]);
      setTotal(0);
      setError('Unable to load snapshot comparison applications.');
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const setFilter = <K extends keyof SnapshotComparisonFilters>(
    key: K,
    value: SnapshotComparisonFilters[K]
  ) => {
    setFilters(previous => ({ ...previous, [key]: value }));
    setPage(0); // Reset pagination when filters change.
  };

  const setQcStatus = (value: TriStateFilter) => setFilter('qcStatus', value);

  const setCenter = (value: CenterFilter) => setFilter('center', value);

  const setChangeDetectionStatus = (value: TriStateFilter) =>
    setFilter('changeDetectionStatus', value);

  const setEscalatedStatus = (value: TriStateFilter) => setFilter('escalatedStatus', value);

  const setAllVerdictFalse = (value: TriStateFilter) => setFilter('allVerdictFalse', value);

  const setExtractionQcStatus = (value: ExtractionQcStatusFilter) =>
    setFilter('extractionQcStatus', value);

  const setDataExtracted = (value: TriStateFilter) => setFilter('dataExtracted', value);

  const setApplicationNumber = (value: string) => setFilter('applicationNumber', value);

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(0);
  };

  const changePage = (nextPage: number) => setPage(nextPage);

  const changePageSize = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(0);
  };

  return {
    rows,
    total,
    loading,
    error,
    page,
    pageSize,
    filters,
    setCenter,
    setQcStatus,
    setChangeDetectionStatus,
    setEscalatedStatus,
    setAllVerdictFalse,
    setExtractionQcStatus,
    setDataExtracted,
    setApplicationNumber,
    clearFilters,
    changePage,
    changePageSize
  };
};

export default useSnapshotComparisonList;
